import React from 'react';
import { System } from '../../enums/System';
import {
  PublicOutlined, FastfoodOutlined, ScaleOutlined, AutoFixHighOutlined, StyleOutlined, LocalShipping,
  EditCalendarOutlined, AdminPanelSettingsOutlined, ViewModuleOutlined, DevicesOtherOutlined, LockOutlined,
  ScheduleSend, EditCalendar, EventRepeat, Task, Category, AccountTree, Newspaper, CurrencyExchange,
  ImageOutlined,
  AssessmentOutlined,
} from '@mui/icons-material';
import {
  Users01, PackageCheck, CoinsSwap02, Calendar, User01, Announcement02,
  Settings01, Grid01, Tag03, CoinsHand,
  Truck01, ListIcon, Building02,
} from '../../assets';
import SettingsSectionsView from './components/SettingsSectionsView';
import { SettingSection } from './settingsTypes';

const SettingsPage: React.FC = () => {

  const settingSections: SettingSection[] = [
    {
      titleKey: 'userAccessManagement',
      descriptionKey: 'userAccessManagementDesc',
      items: [
        {
          translationKey: 'manageApplicationRoles',
          descriptionKey: 'manageApplicationRolesDesc',
          icon: <Users01 />,
          href: '/role-management',
          permissions: ['RolesAdmin', 'RoleManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageUsers',
          descriptionKey: 'manageUsersDesc',
          icon: <User01 />,
          href: '/user-management',
          permissions: ['UserAdmin', 'UserManagementView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'operationsAutomation',
      descriptionKey: 'operationsAutomationDesc',
      items: [
        {
          translationKey: 'countStockSchedule',
          descriptionKey: 'countStockScheduleDesc',
          icon: <PackageCheck />,
          href: '/stock-count-schedule',
          permissions: ['StockCountAdmin', 'StockLocationRead', 'StockCountScheduleRead', 'StockCountUomsRead'],
          systemIds: [System.OrderlyForStores],
          hideInDemo: true,
        },
        {
          translationKey: 'operationalDaySetup',
          descriptionKey: 'operationalDaySetupDesc',
          icon: <EditCalendar />,
          href: '/operational-days',
          permissions: ['OperationalDaysView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          status: 'demo',
          hideInDemo: true,
        },
        {
          translationKey: 'scheduledTasks',
          descriptionKey: 'scheduledTasksDesc',
          icon: <EventRepeat />,
          href: '/task-schedules',
          permissions: ['TaskSchedulesView'],
          systemIds: [System.OrderlyForStores],
          status: 'demo',
        },
        {
          translationKey: 'taskTemplates',
          descriptionKey: 'taskTemplatesDesc',
          icon: <Task />,
          href: '/task-templates',
          permissions: ['TaskTemplatesViewUnrestricted'],
          systemIds: [System.OrderlyForStores],
          status: 'demo',
          hideInDemo: true,
        },
        {
          translationKey: 'scheduledReports',
          descriptionKey: 'scheduledReportsDesc',
          permissions: ['AutomatedTasks'],
          icon: <ScheduleSend />,
          href: '/scheduled-reports',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'automatedWorkflows',
          descriptionKey: 'automatedWorkflowsDesc',
          icon: <AccountTree />,
          href: '/automated-workflows',
          permissions: ['AutomatedWorkflowsView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          status: 'demo',
        },
        {
          translationKey: 'autoshipments',
          descriptionKey: 'autoshipmentsDesc',
          icon: <LocalShipping />,
          href: '/manage-autoshipments/templates',
          permissions: ['AutoshipmentRead'],
          systemIds: [System.OrderlyForStores],
        },
      ],
    },
    {
      titleKey: 'contentManagement',
      descriptionKey: 'contentManagementDesc',
      items: [
        {
          translationKey: 'manageNotifications',
          descriptionKey: 'manageNotificationsDesc',
          icon: <Announcement02 />,
          href: '/manage-notifications',
          permissions: ['NotificationWrite'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          hideInDemo: true,
        },
        {
          translationKey: 'newsManagement',
          descriptionKey: 'newsManagementDesc',
          icon: <Newspaper />,
          href: '/news-management',
          permissions: ['NewsManagementView'],
          status: 'demo',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'banners',
          descriptionKey: 'bannersDesc',
          icon: <ImageOutlined />,
          href: '/settings/content-management/banners',
          permissions: ['ContentManagementAdmin'],
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'itemTags',
          descriptionKey: 'itemTagsDesc',
          icon: <Tag03 />,
          permissions: ['ItemTagView', 'ItemTagAdmin'],
          href: '/item-tags',
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'systemConfiguration',
      descriptionKey: 'systemConfigurationDesc',
      items: [
        {
          translationKey: 'systemConfigurations',
          descriptionKey: 'systemConfigurationsDesc',
          icon: <Settings01 />,
          permissions: ['ConfigurationAdmin'],
          href: '/manage-configurations',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageTransactions',
          descriptionKey: 'manageTransactionsDesc',
          icon: <CoinsSwap02 />,
          href: '/manage-transactions',
          permissions: ['TransactionAdmin'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'manageTaskCategories',
          descriptionKey: 'manageTaskCategoriesDesc',
          icon: <Category />,
          href: '/manage-task-categories',
          permissions: ['TaskCategoriesView'],
          status: 'demo',
          systemIds: [System.OrderlyForStores],
          hideInDemo: true,
        },
        {
          translationKey: 'manageCategory',
          descriptionKey: 'manageCategoryDesc',
          icon: <Grid01 />,
          href: '/manage-categories',
          permissions: ['CategoryAdmin'],
          systemIds: [System.OrderlyForStores],
          hideInDemo: true,
        },
        {
          translationKey: 'uomTypes',
          descriptionKey: 'uomTypesDesc',
          icon: <ScaleOutlined />,
          permissions: ['UomTypeView'],
          href: '/uom-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          hideInDemo: true,
        },

      ],
    },
    {
      titleKey: 'procurement',
      descriptionKey: 'procurementDesc',
      status: 'preview',
      items: [
        {
          translationKey: 'orderChannels',
          descriptionKey: 'orderChannelsDesc',
          icon: <StyleOutlined />,
          permissions: ['OrderTypeView'],
          href: '/procurement/order-channels',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          hideInDemo: true,
        },
        {
          translationKey: 'deliverySchedules',
          descriptionKey: 'deliverySchedulesDesc',
          icon: <EditCalendarOutlined />,
          href: '/manage-delivery-schedules',
          permissions: ['ODSRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'suppliers',
          descriptionKey: 'procurementSuppliersDesc',
          icon: <Truck01 />,
          permissions: ['SupplierView'],
          href: '/procurement/suppliers',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'catalogues',
          descriptionKey: 'procurementCataloguesDesc',
          icon: <ListIcon />,
          permissions: ['CatalogueView'],
          href: '/procurement/catalogues',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'promotions',
          descriptionKey: 'promotionsDesc',
          icon: <CoinsHand />,
          permissions: [ 'PromotionRead', 'PromotionAdmin'],
          href: '/promotions',
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'procurementCategories',
          descriptionKey: 'procurementCategoriesDesc',
          icon: <Tag03 />,
          permissions: ['CategoryView'],
          href: '/procurement/categories',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
          hideInDemo: true,
        },
        {
          translationKey: 'products',
          descriptionKey: 'procurementProductsDesc',
          icon: <FastfoodOutlined />,
          permissions: ['ProductView'],
          href: '/procurement/products',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'rangeOverview',
          descriptionKey: 'rangeOverviewDesc',
          icon: <AssessmentOutlined />,
          permissions: ['RangeOverviewRead'],
          href: '/range-planning/range-overview',
          status: 'demo',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'productCatalogManagement',
      descriptionKey: 'productCatalogManagementDesc',
      hideInDemo: true,
      items: [
        {
          translationKey: 'orderTypes',
          descriptionKey: 'orderTypesDesc',
          icon: <StyleOutlined />,
          permissions: ['OrderTypeView'],
          href: '/order-types',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'manageDeliverySchedules',
          descriptionKey: 'manageDeliverySchedulesDesc',
          icon: <EditCalendarOutlined />,
          href: '/manage-delivery-schedules',
          permissions: ['ODSRead'],
          systemIds: [System.OrderlyForStores],
        },
        {
          translationKey: 'deliveryPricing',
          descriptionKey: 'deliveryPricingDesc',
          icon: <CurrencyExchange />,
          href: '/settings/delivery-pricing',
          permissions: ['DeliveryPricingView'],
          systemIds: [System.OrderlyForDistributors],
        },
        {
          translationKey: 'suppliers',
          descriptionKey: 'suppliersDesc',
          icon: <Truck01 />,
          permissions: ['SupplierView'],
          href: '/suppliers',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'catalogues',
          descriptionKey: 'cataloguesDesc',
          icon: <ListIcon />,
          permissions: ['CatalogueView'],
          href: '/catalogues',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'productCategories',
          descriptionKey: 'productCategoriesDesc',
          icon: <Tag03 />,
          permissions: ['CategoryView'],
          href: '/categories',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'products',
          descriptionKey: 'productsDesc',
          icon: <FastfoodOutlined />,
          permissions: ['ProductView'],
          href: '/products',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'promotions',
          descriptionKey: 'promotionsDesc',
          icon: <CoinsHand />,
          permissions: [ 'PromotionRead', 'PromotionAdmin'],
          href: '/promotions',
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'advancedFeatures',
      descriptionKey: 'advancedFeaturesDesc',
      items: [
        {
          translationKey: 'podSettings',
          descriptionKey: 'podSettingsDesc',
          icon: <AutoFixHighOutlined />,
          permissions: ['AIOrderingAdmin'],
          href: '/manage-pod-configurations',
          systemIds: [System.OrderlyForStores],
          requiredModule: 'Predictive Ordering Settings',
        },
        {
          translationKey: 'manageExchangeRates',
          descriptionKey: 'manageExchangeRatesDesc',
          icon: <CurrencyExchange />,
          href: '/manage-exchange-rates',
          permissions: ['ConfigurationAdmin'],
          systemIds: [System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'businessStructure',
      descriptionKey: 'businessStructureDesc',
      items: [
        {
          translationKey: 'instances',
          descriptionKey: 'instancesDesc',
          icon: <Grid01 />,
          permissions: ['InstanceView'],
          href: '/instances',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'markets',
          descriptionKey: 'marketsDesc',
          icon: <PublicOutlined />,
          permissions: ['MarketView'],
          href: '/markets',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        /*  {
            translationKey: 'storeGroups',
            descriptionKey: 'storeGroupsDesc',
            icon: <PublicOutlined />,
            permissions: ['NEWPERMISSION'],
            href: '/markets',
            systemIds: [System.OrderlyForStores],
          }, */
        {
          translationKey: 'stores',
          descriptionKey: 'storesDesc',
          icon: <Building02 />,
          permissions: ['StoreView'],
          href: '/stores',
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
    {
      titleKey: 'platformAdministration',
      descriptionKey: 'platformAdministrationDesc',
      items: [
        {
          translationKey: 'tenants',
          descriptionKey: 'tenantsDesc',
          icon: <AdminPanelSettingsOutlined />,
          href: '/admin/tenants',
          permissions: ['TenantView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'modules',
          descriptionKey: 'modulesDesc',
          icon: <ViewModuleOutlined />,
          href: '/admin/modules',
          permissions: ['ModuleView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'systems',
          descriptionKey: 'systemsDesc',
          icon: <DevicesOtherOutlined />,
          href: '/admin/systems',
          permissions: ['SystemView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
        {
          translationKey: 'permissions',
          descriptionKey: 'permissionsDesc',
          icon: <LockOutlined />,
          href: '/admin/permissions',
          permissions: ['PermissionView'],
          systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        },
      ],
    },
  ];

  return (
    <SettingsSectionsView
      titleKey="settings"
      descriptionKey="settingsDescription"
      descriptionDefault="Manage your application settings, configurations, and preferences from this central location."
      sections={settingSections}
    />
  );
};

export default SettingsPage;